import { IView } from '../../../../Assembler/dist/registry/iface/iView.js';
import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { BaseView } from './baseView.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';

/**
 * Note: RuleView implements both IView and IViewJson interfaces.
 * The engine will prefer getViewJson for Json engines (avoids conversion overhead).
 */

// Type prefix constants for new format
const TYPE_HTML = 's';
const META_KEY = '_meta';

/**
 * HtmlJson metadata structure
 */
interface HtmlJsonMeta {
    hasJsonKeys: boolean;
    jsonKeys: string[];
    hasPlaceholders: boolean;
    placeholders: string[];
    hasSlotholders: boolean;
    slotholders: string[];
    hasArrayBlocks: boolean;
    arrayBlocks: string[];
}

/**
 * Checks if this is a type-prefixed key (new format: type char + digit, e.g., "k2_Title", "s1")
 */
function isTypePrefixedKey(key: string): boolean {
    return key.length >= 2 && /\d/.test(key[1]);
}

/**
 * Creates a metadata object for new format
 */
function createMeta(): HtmlJsonMeta {
    return {
        hasJsonKeys: false,
        jsonKeys: [],
        hasPlaceholders: false,
        placeholders: [],
        hasSlotholders: false,
        slotholders: [],
        hasArrayBlocks: false,
        arrayBlocks: []
    };
}

// Content type constants
const ContentTypeAppFile = 'AppFile';
const ContentTypePlaceholder = 'Placeholder';
const ContentTypeSlotHolder = 'SlotHolder';
const ContentTypeSlot = 'Slot';

// AppFile styling (Purple)
const AppFileBorderColor = '#667eea';
const AppFileBackgroundGradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

// Placeholder styling (Blue)
const PlaceholderBorderColor = '#3b82f6';
const PlaceholderBackgroundGradient = 'linear-gradient(135deg, #3b82f6 0%, #0ea5e9 100%)';

// SlotHolder styling (Green)
const SlotHolderBorderColor = '#10b981';
const SlotHolderBackgroundGradient = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';

// Slot styling (Teal/Cyan - for individual slot content within SlotHolders)
const SlotBorderColor = '#0891b2';
const SlotBackgroundGradient = 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)';

// SearchAppSite styling (Orange/Amber - highlights components from different AppSites)
const DefaultSearchAppSiteColor = '#d97706';

// JsonKey styling (Orange/Amber - for JSON key value replacement)
const JsonKeyBorderColor = '#d97706';

// JsonArray styling (Teal - for JSON array iteration)
const JsonArrayBorderColor = '#0d9488';

// Conditional styling (Rose - for boolean conditional blocks)
const ConditionalBorderColor = '#e11d48';

// The ONE responsive stylesheet for the decorator card, emitted once at the top of every AppFile-level
// wrapper. It is FIXED text (no per-component interpolation). Element colours stay inline where they
// generate; only layout that needs a media query / flex row / pseudo-state lives here:
//   .av-dec button     buttons default to full width; inside .av-pager they shrink to a wrapping row
//   .av-pager          the per-AppSite pagination controls flow in a flex row and wrap on narrow screens
//   .av-dec table      bordered + striped presentation for any decorator-hosted table
//   @media <=600px     trims the heavy card padding so content keeps usable width on a phone
const DecoratorStyleBlock =
    '<style>\n' +
    '.av-dec button{display:block;width:100%;padding:10px 20px;border:2px solid #0d9488;border-radius:6px;background:linear-gradient(135deg,#0d948822 0%,#0d948811 100%);font-size:1.1em;color:#0d9488;cursor:pointer;font-weight:600;min-height:44px;transition:all .2s;box-shadow:0 1px 3px rgba(0,0,0,0.1)}\n' +
    '.av-dec button:disabled{border-color:#cbd5e1;background:#f1f5f9;color:#94a3b8;cursor:not-allowed;box-shadow:none}\n' +
    '.av-dec .av-pager{display:flex;flex-wrap:wrap;gap:8px;align-items:stretch;margin:5px 0}\n' +
    '.av-dec .av-pager>div{flex:0 1 auto;margin:0}\n' +
    '.av-dec .av-pager button{display:inline-block;width:auto}\n' +
    '.av-dec table{border-collapse:collapse;width:100%;font-size:0.9em}\n' +
    '.av-dec th,.av-dec td{border:1px solid #cbd5e1;padding:6px 10px;text-align:left}\n' +
    '.av-dec thead th{background:#eef2f7;color:#334155;font-weight:600}\n' +
    '.av-dec tbody tr:nth-child(even){background:#f8fafc}\n' +
    '.av-dec tbody tr:hover{background:#eef6ff}\n' +
    '.av-dec .av-tablebox{overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:thin;scrollbar-color:#94a3b8 #f1f5f9}\n' +
    '.av-dec .av-tablebox::-webkit-scrollbar{height:10px}\n' +
    '.av-dec .av-tablebox::-webkit-scrollbar-track{background:#f1f5f9;border-radius:5px}\n' +
    '.av-dec .av-tablebox::-webkit-scrollbar-thumb{background:#94a3b8;border-radius:5px}\n' +
    '@media (max-width:600px){.av-dec-outer{padding:14px!important;margin:18px auto!important}.av-dec-outer>div{padding:16px!important}}\n' +
    '</style>\n';

/**
 * Rule-based implementation of IView with type-based styling
 */
export class RuleView extends IView {
    /**
     * Gets the full type name for DynamicView operation tracking
     */
    get typeName(): string {
        return 'RuleView';
    }

    private static getStyleParams(paramJson: JsonObject, contentType: string): [string, string] {
        switch (contentType) {
            case ContentTypeAppFile:
                return [
                    RuleView.getStringParam(paramJson, 'AppFileBorderColor', AppFileBorderColor),
                    RuleView.getStringParam(paramJson, 'AppFileBackgroundGradient', AppFileBackgroundGradient)
                ];
            case ContentTypePlaceholder:
                return [
                    RuleView.getStringParam(paramJson, 'PlaceholderBorderColor', PlaceholderBorderColor),
                    RuleView.getStringParam(paramJson, 'PlaceholderBackgroundGradient', PlaceholderBackgroundGradient)
                ];
            case ContentTypeSlotHolder:
                return [
                    RuleView.getStringParam(paramJson, 'SlotHolderBorderColor', SlotHolderBorderColor),
                    RuleView.getStringParam(paramJson, 'SlotHolderBackgroundGradient', SlotHolderBackgroundGradient)
                ];
            case ContentTypeSlot:
                return [
                    RuleView.getStringParam(paramJson, 'SlotBorderColor', SlotBorderColor),
                    RuleView.getStringParam(paramJson, 'SlotBackgroundGradient', SlotBackgroundGradient)
                ];
            default:
                return [
                    RuleView.getStringParam(paramJson, 'BorderColor', AppFileBorderColor),
                    RuleView.getStringParam(paramJson, 'BackgroundGradient', AppFileBackgroundGradient)
                ];
        }
    }

    /**
     * Gets dynamic view for template processing with type-based styling
     */
    getView(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtml: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null = null
    ): string {
        let retHtml = componentHtml;

        try {
            // Validate that template has no HTML/style tags (should be plain)
            if (RuleView.hasHtmlStyleTags(componentHtml)) {
                return '{{HavingHTMLStyle}}';
            }

            // Apply ReplaceKey/ReplaceText replacement if specified in paramJson
            retHtml = BaseView.applyReplacement(retHtml, paramJson);

            // Determine content type by comparing appFile with componentName
            const contentType = RuleView.determineContentType(appFile, componentName, componentHtml, paramJson);

            const [borderColor, backgroundGradient] = RuleView.getStyleParams(paramJson, contentType);

            const searchAppSiteColor = RuleView.getStringParam(paramJson, 'SearchAppSiteColor', DefaultSearchAppSiteColor);
            const defaultTitle = !appView ? `${appSite} AppSite` : `${appSite}/${appView} AppSite`;
            const title = RuleView.getStringParam(paramJson, 'Title', defaultTitle);
            let description = RuleView.getOptionalStringParam(paramJson, 'Description');
            if (!description) {
                description = RuleView.generateAutoDescription(contentType, componentName, componentHtml, appSite, resolvedAppSite, searchAppSiteColor);
            }
            const componentId = BaseView.getComponentId(paramJson, componentJson, componentName);

            // Wrap JsonKey markers with styling (before merge replaces them)
            const jsonKeyBorderColor = RuleView.getStringParam(paramJson, 'JsonKeyBorderColor', JsonKeyBorderColor);
            const skipJsonKeysStr = RuleView.getStringParam(paramJson, 'SkipJsonKeys', 'Title,Description');
            const skipJsonKeys = RuleView.parseSkipKeys(skipJsonKeysStr);
            retHtml = RuleView.wrapJsonKeys(retHtml, jsonKeyBorderColor, skipJsonKeys);

            // Wrap JSON array blocks with styling (before merge resolves them)
            const jsonArrayBorderColor = RuleView.getStringParam(paramJson, 'JsonArrayBorderColor', JsonArrayBorderColor);
            retHtml = RuleView.wrapJsonArrays(retHtml, jsonArrayBorderColor);

            // Wrap conditional blocks with styling (before merge resolves them)
            const conditionalBorderColor = RuleView.getStringParam(paramJson, 'ConditionalBorderColor', ConditionalBorderColor);
            retHtml = RuleView.wrapConditionals(retHtml, conditionalBorderColor);

            // Wrap individual slot markers with Slot styling (before merge replaces them)
            if (contentType === ContentTypeSlotHolder) {
                const slotBorderColor = RuleView.getStringParam(paramJson, 'SlotBorderColor', SlotBorderColor);
                retHtml = RuleView.wrapSlotMarkers(retHtml, slotBorderColor);
            }

            // Style any <select> elements in the template. Buttons are NOT styled inline: the decorator
            // <style> block styles them via `.av-dec button` (+ `:disabled`, + the `.av-pager` row rules), so
            // both the string and JSON engines emit the raw <button> and stay byte-identical — a button nested
            // in a conditional block, which the JSON engine cannot reach to style inline, then matches too.
            retHtml = RuleView.styleAllSelectElements(retHtml, jsonArrayBorderColor);

            // Generate styled wrapper based on content type
            retHtml = RuleView.wrapWithStyle(retHtml, contentType, componentId, title, description, borderColor, backgroundGradient);
        } catch (ex) {
            Logger.error((ex as Error).message, 'RuleView');
        }

        return retHtml;
    }

    /**
     * Gets dynamic view wrapper for HtmlJson structure (avoids HTML string conversion)
     */
    getViewJson(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtmlJson: JsonObject,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null = null
    ): JsonObject {
        try {
            // Validate that template has no HTML/style tags (should be plain)
            if (RuleView.hasHtmlStyleTagsInJson(componentHtmlJson)) {
                const errorResult = new JsonObject();
                errorResult.set(`${TYPE_HTML}0`, '{{HavingHTMLStyle}}');
                errorResult.set(META_KEY, createMeta());
                return errorResult;
            }

            // Determine content type by analyzing the HtmlJson structure
            const contentType = RuleView.determineContentTypeFromJson(appFile, componentName, componentHtmlJson, paramJson);

            const [borderColor, backgroundGradient] = RuleView.getStyleParams(paramJson, contentType);

            const searchAppSiteColor = RuleView.getStringParam(paramJson, 'SearchAppSiteColor', DefaultSearchAppSiteColor);
            const defaultTitleJson = !appView ? `${appSite} AppSite` : `${appSite}/${appView} AppSite`;
            const title = RuleView.getStringParam(paramJson, 'Title', defaultTitleJson);
            let description = RuleView.getOptionalStringParam(paramJson, 'Description');
            if (!description) {
                description = RuleView.generateAutoDescriptionFromJson(contentType, componentName, componentHtmlJson, appSite, resolvedAppSite, searchAppSiteColor);
            }
            const componentId = BaseView.getComponentId(paramJson, componentJson, componentName);

            // JsonKey wrapping configuration
            const jsonKeyBorderColorJson = RuleView.getStringParam(paramJson, 'JsonKeyBorderColor', JsonKeyBorderColor);
            const skipJsonKeysStrJson = RuleView.getStringParam(paramJson, 'SkipJsonKeys', 'Title,Description');
            const skipJsonKeysJson = RuleView.parseSkipKeys(skipJsonKeysStrJson);
            // Conditional wrapping configuration
            const conditionalBorderColorJson = RuleView.getStringParam(paramJson, 'ConditionalBorderColor', ConditionalBorderColor);
            // JsonArray wrapping configuration
            const jsonArrayBorderColorJson = RuleView.getStringParam(paramJson, 'JsonArrayBorderColor', JsonArrayBorderColor);

            // Process the HtmlJson structure
            const result = new JsonObject();
            let sequence = 1;

            // Copy _meta first if present (csharp shape: preserve input meta so hasPlaceholders survives the wrap)
            if (componentHtmlJson.has(META_KEY)) {
                result.set(META_KEY, componentHtmlJson.get(META_KEY));
            }

            // Add opening wrapper based on content type
            const openingWrapper = RuleView.getOpeningWrapperJson(contentType, componentId, title, description, borderColor, backgroundGradient);
            result.set(`${TYPE_HTML}${sequence}`, openingWrapper);
            sequence++;

            // Track if we need to absorb a closing </select> or </button> from next 's' entry
            let absorbClosingSelect = false;
            let absorbClosingButton = false;

            // Simple pass-through of all entries
            for (const key of componentHtmlJson.keys()) {
                // Skip metadata
                if (key === META_KEY) continue;

                const jsonVal: JsonValue = componentHtmlJson.get(key);

                // Get type from first character (new format)
                const keyType = key.length > 0 ? key[0] : '\0';

                const strVal = jsonVal.asString();
                if (keyType === 's' && strVal !== null) {
                    // Html content - apply replacement and style form elements
                    let htmlContent = BaseView.applyReplacement(strVal, paramJson);

                    // If we need to absorb a closing </select> or </button>, handle it
                    if (absorbClosingSelect) {
                        absorbClosingSelect = false;
                        const closeIdx = htmlContent.toLowerCase().indexOf('</select>');
                        if (closeIdx >= 0) {
                            const beforeClose = htmlContent.substring(0, closeIdx);
                            const selectClose = '</select>';
                            const afterClose = htmlContent.substring(closeIdx + selectClose.length);

                            result.set(`s${sequence}`, beforeClose + selectClose + '</div>\n</div>');
                            sequence++;

                            if (afterClose) {
                                const afterContent = RuleView.styleAllSelectElements(afterClose, jsonArrayBorderColorJson);
                                result.set(`s${sequence}`, afterContent);
                                sequence++;
                            }
                            continue;
                        }
                    }
                    if (absorbClosingButton) {
                        absorbClosingButton = false;
                        const closeIdx = htmlContent.toLowerCase().indexOf('</button>');
                        if (closeIdx >= 0) {
                            const beforeClose = htmlContent.substring(0, closeIdx);
                            const buttonClose = '</button>';
                            const afterClose = htmlContent.substring(closeIdx + buttonClose.length);

                            result.set(`s${sequence}`, beforeClose + buttonClose + '</div>\n</div>');
                            sequence++;

                            if (afterClose) {
                                const afterContent = RuleView.styleAllSelectElements(afterClose, jsonArrayBorderColorJson);
                                result.set(`s${sequence}`, afterContent);
                                sequence++;
                            }
                            continue;
                        }
                    }

                    htmlContent = RuleView.styleAllSelectElements(htmlContent, jsonArrayBorderColorJson);
                    result.set(`s${sequence}`, htmlContent);
                    sequence++;
                } else if (keyType === 'x' && strVal !== null) {
                    // SlotHtml content - pass through
                    result.set(`x${sequence}`, strVal);
                    sequence++;
                } else if (keyType === 'i' && contentType === ContentTypeSlotHolder && strVal !== null) {
                    // Slot injection point inside a SlotHolder - wrap with Slot styling
                    const slotBorderColor = RuleView.getStringParam(paramJson, 'SlotBorderColor', SlotBorderColor);
                    const slotValue = strVal;
                    let slotName = '';
                    if (slotValue.startsWith('{{$') && slotValue.endsWith('}}')) {
                        slotName = slotValue.substring(3, slotValue.length - 2);
                    }

                    const slotOpening = `<div style="border: 1px solid ${slotBorderColor}; border-radius: 4px; padding: 8px; margin: 4px 0; background: #ecfeff;">`
                        + `\n    <p style="margin: 0 0 5px 0; font-size: 0.85em; color: ${slotBorderColor}; font-weight: 500;">`
                        + `\n        Slot [${slotName}]`
                        + `\n    </p>`
                        + `\n    <div style="background: white; padding: 8px; border-radius: 4px;">`
                        + `\n        `;
                    result.set(`s${sequence}`, slotOpening);
                    sequence++;

                    // Extract name if present
                    let keyName = '';
                    const underscorePos = key.indexOf('_');
                    if (underscorePos >= 0) {
                        keyName = key.substring(underscorePos + 1);
                    }
                    const newKey = !keyName
                        ? `i${sequence}`
                        : `i${sequence}_${keyName}`;
                    result.set(newKey, slotValue);
                    sequence++;

                    const slotClosing = '\n    </div>\n</div>';
                    result.set(`s${sequence}`, slotClosing);
                    sequence++;
                } else if (keyType === 'k' && strVal !== null) {
                    // JsonKey entry - wrap with styling unless in skip list
                    let keyName = '';
                    const underscorePos = key.indexOf('_');
                    if (underscorePos >= 0) {
                        keyName = key.substring(underscorePos + 1);
                    }

                    if (keyName && !skipJsonKeysJson.has(keyName)) {
                        // Wrap with JsonKey styling
                        const jsonKeyOpening = `<div id="${RuleView.toCamelCase(keyName)}" style="border: 2px dashed ${jsonKeyBorderColorJson}; border-radius: 6px; padding: 5px; margin: 5px 0; background: #fffbeb;">`
                            + `\n    <p style="margin: 0 0 5px 0; color: ${jsonKeyBorderColorJson}; font-weight: 500; font-size: 0.85em;">JsonKey [${keyName}]</p>`
                            + `\n    <div style="background: white; padding: 8px; border-radius: 4px;">`;
                        result.set(`s${sequence}`, jsonKeyOpening);
                        sequence++;

                        const newJsonKey = `k${sequence}_${keyName}`;
                        result.set(newJsonKey, strVal);
                        sequence++;

                        const jsonKeyClosing = '</div>\n</div>';
                        result.set(`s${sequence}`, jsonKeyClosing);
                        sequence++;
                    } else {
                        // Skip-listed key - pass through without wrapping
                        const newKey2 = !keyName
                            ? `k${sequence}`
                            : `k${sequence}_${keyName}`;
                        result.set(newKey2, strVal);
                        sequence++;
                    }
                } else if (keyType === 'c') {
                    // Conditional entry - wrap with Conditional styling
                    const conditionalData = jsonVal.asObject();
                    let conditionalKey = '';
                    if (conditionalData) {
                        conditionalKey = conditionalData.get('ConditionalKey').asString() ?? '';
                    }

                    const hasNegBlock = conditionalData && !conditionalData.get('NegativeBlock').isNull;
                    const conditionalLabel = hasNegBlock
                        ? `Conditional [${conditionalKey}] (True/False)`
                        : `Conditional [${conditionalKey}]`;
                    const conditionalOpening = `<div id="${RuleView.toCamelCase(conditionalKey)}" style="border: 2px dashed ${conditionalBorderColorJson}; border-radius: 6px; padding: 5px; margin: 5px 0; background: #fff1f2;">`
                        + `\n    <p style="margin: 0 0 5px 0; color: ${conditionalBorderColorJson}; font-weight: 500; font-size: 0.85em;">${conditionalLabel}</p>`
                        + `\n    <div style="background: white; padding: 8px; border-radius: 4px;">`;
                    result.set(`s${sequence}`, conditionalOpening);
                    sequence++;

                    // Extract name if present
                    let keyName = '';
                    const underscorePos = key.indexOf('_');
                    if (underscorePos >= 0) {
                        keyName = key.substring(underscorePos + 1);
                    }
                    const newKey3 = !keyName
                        ? `c${sequence}`
                        : `c${sequence}_${keyName}`;
                    result.set(newKey3, jsonVal);
                    sequence++;

                    const conditionalClosing = '</div>\n</div>';
                    result.set(`s${sequence}`, conditionalClosing);
                    sequence++;
                } else if (keyType === 'a') {
                    // Array block entry
                    let keyName = '';
                    const underscorePos = key.indexOf('_');
                    if (underscorePos >= 0) {
                        keyName = key.substring(underscorePos + 1);
                    }

                    if (RuleView.isPrecededByOpenTableJson(result)) {
                        // Array inside a <table> ({{@Columns}}/{{@Rows}}/{{@Cells}}): leave it UNWRAPPED,
                        // exactly like the string engine's wrapJsonArrays — a per-array annotation <div> inside
                        // <tr>/<thead>/<tbody> is illegal and the browser foster-parents it out, detaching the
                        // label. The template's <div class="av-tablebox"> + the decorator <style> block supply
                        // the scroll container and bordered/striped presentation, so the raw array merges in
                        // place and the table renders clean.
                        const rawKey = !keyName
                            ? `a${sequence}`
                            : `a${sequence}_${keyName}`;
                        result.set(rawKey, jsonVal);
                        sequence++;
                    } else {
                        // Array - wrap with JsonArray styling
                        const arrayLabel = `JsonArray [${keyName}]`;
                        const arrayOpening = `<div id="${RuleView.toCamelCase(keyName)}" style="border: 2px dashed ${jsonArrayBorderColorJson}; border-radius: 6px; padding: 5px; margin: 5px 0; background: #f0fdfa;">`
                            + `\n    <p style="margin: 0 0 5px 0; color: ${jsonArrayBorderColorJson}; font-weight: 500; font-size: 0.85em;">${arrayLabel}</p>`
                            + `\n    <div style="background: white; padding: 8px; border-radius: 4px;">`;

                        const relocatedSelect = RuleView.tryRelocateSplitPrecedingElement(result, sequence, 'select', arrayOpening, jsonArrayBorderColorJson);
                        if (relocatedSelect.relocated) {
                            sequence = relocatedSelect.sequence;
                            absorbClosingSelect = true;
                        } else {
                            const relocatedButton = RuleView.tryRelocateSplitPrecedingElement(result, sequence, 'button', arrayOpening, jsonArrayBorderColorJson);
                            if (relocatedButton.relocated) {
                                sequence = relocatedButton.sequence;
                                absorbClosingButton = true;
                            } else {
                                const precedingSelectHtml = RuleView.findAndRemovePrecedingElement(result, sequence, 'select');
                                const precedingButtonHtml = RuleView.findAndRemovePrecedingElement(result, sequence, 'button');

                                if (precedingSelectHtml !== null) {
                                    const selectToUse = precedingSelectHtml.toLowerCase().indexOf('style=') < 0
                                        ? RuleView.styleSelectElement(precedingSelectHtml, jsonArrayBorderColorJson)
                                        : precedingSelectHtml;
                                    result.set(`s${sequence}`, arrayOpening + selectToUse);
                                    sequence++;
                                    absorbClosingSelect = true;
                                } else if (precedingButtonHtml !== null) {
                                    const buttonToUse = precedingButtonHtml.toLowerCase().indexOf('style=') < 0
                                        ? RuleView.styleButtonElement(precedingButtonHtml, jsonArrayBorderColorJson)
                                        : precedingButtonHtml;
                                    result.set(`s${sequence}`, arrayOpening + buttonToUse);
                                    sequence++;
                                    absorbClosingButton = true;
                                } else {
                                    result.set(`s${sequence}`, arrayOpening);
                                    sequence++;
                                }
                            }
                        }

                        const newKey4 = !keyName
                            ? `a${sequence}`
                            : `a${sequence}_${keyName}`;
                        result.set(newKey4, jsonVal);
                        sequence++;

                        // Only add closing wrapper if not absorbing a closing tag
                        if (!absorbClosingSelect && !absorbClosingButton) {
                            const arrayClosing = '</div>\n</div>';
                            result.set(`s${sequence}`, arrayClosing);
                            sequence++;
                        }
                    }
                } else {
                    // Copy other entries preserving their key format
                    let keyName = '';
                    const underscorePos = key.indexOf('_');
                    if (underscorePos >= 0) {
                        keyName = key.substring(underscorePos + 1);
                    }

                    const newKey5 = !keyName
                        ? `${keyType}${sequence}`
                        : `${keyType}${sequence}_${keyName}`;
                    result.set(newKey5, jsonVal);
                    sequence++;
                }
            }

            // Add closing wrapper based on content type
            const closingWrapper = RuleView.getClosingWrapperJson(contentType);
            result.set(`${TYPE_HTML}${sequence}`, closingWrapper);

            return result;
        } catch (ex) {
            Logger.error((ex as Error).message, 'RuleView.GetViewJson');
            return componentHtmlJson;
        }
    }

    /**
     * Checks if HtmlJson structure contains HTML/style tags
     */
    static hasHtmlStyleTagsInJson(htmlJson: JsonObject): boolean {
        for (const key of htmlJson.keys()) {
            if (key === META_KEY) continue;

            // Get type char for format detection
            const keyType = isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0';

            // Check if it's an HTML type (new format 's' or old format 'Html')
            let isHtmlType = false;
            if (keyType === TYPE_HTML) {
                isHtmlType = true;
            } else {
                // Check old format
                const underscoreIndex = key.indexOf('_');
                const oldKeyType = underscoreIndex >= 0 ? key.substring(underscoreIndex + 1) : key;
                if (oldKeyType.startsWith('Html')) {
                    isHtmlType = true;
                }
            }

            const strVal = htmlJson.get(key).asString();
            if (isHtmlType && strVal !== null) {
                if (RuleView.hasHtmlStyleTags(strVal)) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Determines content type from HtmlJson structure
     */
    static determineContentTypeFromJson(appFile: string, componentName: string, htmlJson: JsonObject, paramJson: JsonObject): string {
        // Check for explicit ContentType override in paramJson
        if (paramJson && paramJson.containsKey('ContentType')) {
            const contentType = paramJson.getString('ContentType');
            if (contentType && contentType.length > 0) {
                return contentType;
            }
        }

        // 1. AppFile: appFile matches componentName (root component)
        if (appFile.toLowerCase() === componentName.toLowerCase()) {
            return ContentTypeAppFile;
        }

        // 2. SlotHolder: Contains Slot entries
        for (const key of htmlJson.keys()) {
            if (key === META_KEY) continue;

            const keyType = isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0';

            // Check new format slot type 'i'
            if (keyType === 'i') {
                return ContentTypeSlotHolder;
            }

            // Check old format
            const underscoreIndex = key.indexOf('_');
            const oldKeyType = underscoreIndex >= 0 ? key.substring(underscoreIndex + 1) : key;
            if (oldKeyType.startsWith('Slot_')) {
                return ContentTypeSlotHolder;
            }
        }

        // 3. Placeholder: Default for all other components
        return ContentTypePlaceholder;
    }

    /**
     * Generates auto-description from HtmlJson structure
     */
    static generateAutoDescriptionFromJson(contentType: string, componentName: string, htmlJson: JsonObject, appSite: string, resolvedAppSite: string, searchAppSiteColor: string): string | null {
        const placeholders: string[] = [];
        const slotHolders: string[] = [];
        const slots: string[] = [];
        const jsonArrays: string[] = [];
        const jsonKeys: string[] = [];

        // Extract information from HtmlJson structure recursively
        RuleView.extractComponentsFromHtmlJson(htmlJson, placeholders, slotHolders, slots, jsonArrays, jsonKeys);

        return RuleView.buildAutoDescription(contentType, componentName, appSite, resolvedAppSite, searchAppSiteColor, placeholders, slotHolders, slots, jsonArrays, jsonKeys);
    }

    /**
     * Builds the auto-description string from the extracted template marker lists.
     * Shared tail of generateAutoDescription (HTML markers) and generateAutoDescriptionFromJson (HtmlJson markers).
     */
    static buildAutoDescription(contentType: string, componentName: string, appSite: string, resolvedAppSite: string, searchAppSiteColor: string, placeholders: string[], slotHolders: string[], slots: string[], jsonArrays: string[], jsonKeys: string[]): string | null {
        // Build appSite suffix if resolved from a different appSite
        let appSiteSuffix = '';
        if (resolvedAppSite && resolvedAppSite.length > 0 && appSite.toLowerCase() !== resolvedAppSite.toLowerCase()) {
            appSiteSuffix = ` <span style="color: ${searchAppSiteColor}; font-weight: 600;">(${resolvedAppSite})</span>`;
        }

        if (contentType === ContentTypeSlotHolder) {
            return `SlotHolder Component [${componentName}]${appSiteSuffix} with Slots [${slots.join(', ')}]`;
        }

        if (contentType === ContentTypeSlot) {
            return `Slot [${componentName}]${appSiteSuffix}`;
        }

        if (contentType === ContentTypePlaceholder) {
            const parts = [`Placeholder Component [${componentName}]${appSiteSuffix}`];

            if (placeholders.length > 0 || slotHolders.length > 0 || jsonArrays.length > 0 || jsonKeys.length > 0) {
                const holderParts: string[] = [];
                if (placeholders.length > 0) holderParts.push(placeholders.join(', '));
                if (slotHolders.length > 0) holderParts.push(slotHolders.join(', '));
                if (jsonArrays.length > 0) holderParts.push(jsonArrays.join(', '));
                if (jsonKeys.length > 0) holderParts.push(jsonKeys.join(', '));
                parts.push(`with [${holderParts.join(', ')}]`);
            }

            return parts.join(' ');
        }

        // For AppFile type
        if (placeholders.length === 0 && slotHolders.length === 0 && slots.length === 0 && jsonArrays.length === 0 && jsonKeys.length === 0) {
            if (appSiteSuffix.length > 0) {
                return `${componentName}${appSiteSuffix}`;
            }
            return null;
        }

        const descParts = [`${componentName}${appSiteSuffix}`];

        if (placeholders.length > 0) {
            descParts.push(`with PlaceHolders [${placeholders.join(', ')}]`);
        }
        if (slotHolders.length > 0) {
            if (placeholders.length > 0) {
                descParts.push(`and SlotHolders [${slotHolders.join(', ')}]`);
            } else {
                descParts.push(`with SlotHolders [${slotHolders.join(', ')}]`);
            }
        }
        if (jsonKeys.length > 0) {
            if (placeholders.length > 0 || slotHolders.length > 0) {
                descParts.push(`and JsonKeys [${jsonKeys.join(', ')}]`);
            } else {
                descParts.push(`with JsonKeys [${jsonKeys.join(', ')}]`);
            }
        }
        if (jsonArrays.length > 0) {
            if (placeholders.length > 0 || slotHolders.length > 0 || jsonKeys.length > 0) {
                descParts.push(`and JsonArrays [${jsonArrays.join(', ')}]`);
            } else {
                descParts.push(`with JsonArrays [${jsonArrays.join(', ')}]`);
            }
        }

        return descParts.join(' ');
    }

    /**
     * Gets opening wrapper HTML based on content type
     */
    static getOpeningWrapperJson(contentType: string, componentId: string, title: string, description: string | null, borderColor: string, backgroundGradient: string): string {
        const descriptionHtml = !description ? '' :
            `\n        <p style="color: #666; margin: 0 0 15px 0; font-size: 0.95em;">${description}</p>`;

        switch (contentType) {
            case ContentTypeAppFile:
                return `${DecoratorStyleBlock}<div id="${componentId}" class="av-dec-outer" role="region" aria-label="${title}" style="max-width: 800px; margin: 40px auto; padding: 30px; background: ${backgroundGradient}; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.1);">
    <div style="background: white; padding: 30px; border-radius: 8px;">
        <h2 style="color: ${borderColor}; margin: 0 0 10px 0; font-size: 1.8em;">${title}</h2>${descriptionHtml}
        <div class="av-dec" style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid ${borderColor};">
            `;

            case ContentTypePlaceholder:
                return `<div id="${componentId}" role="region" aria-label="${description}" style="border: 2px dashed ${borderColor}; border-radius: 6px; padding: 5px; background: #eff6ff;">
    <p style="margin: 0; text-align: center; color: ${borderColor}; font-weight: 500;">
        ${description}
    </p>
    <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid ${borderColor};">
        `;

            case ContentTypeSlotHolder:
                return `<div id="${componentId}" role="region" aria-label="${description}" style="border: 2px dashed ${borderColor}; border-radius: 6px; padding: 5px; background: #ecfdf5;">
    <p style="margin: 0; text-align: center; color: ${borderColor}; font-weight: 500;">
        ${description}
    </p>
    <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid ${borderColor};">
        `;

            case ContentTypeSlot:
                return `<div id="${componentId}" role="region" aria-label="${description}" style="border: 1px solid ${borderColor}; border-radius: 4px; padding: 8px; margin: 4px 0; background: #ecfeff;">
    <p style="margin: 0 0 5px 0; font-size: 0.85em; color: ${borderColor}; font-weight: 500;">
        ${description}
    </p>
    <div style="background: white; padding: 8px; border-radius: 4px;">
        `;

            default:
                return `<div id="${componentId}" style="border-left: 4px solid ${borderColor}; padding: 10px; margin: 5px 0;">
    `;
        }
    }

    /**
     * Gets closing wrapper HTML based on content type
     */
    static getClosingWrapperJson(contentType: string): string {
        switch (contentType) {
            case ContentTypeAppFile:
                return `
        </div>
    </div>
</div>`;
            case ContentTypePlaceholder:
            case ContentTypeSlotHolder:
                return `
    </div>
</div>`;
            case ContentTypeSlot:
                return `
    </div>
</div>`;
            default:
                return '</div>';
        }
    }

    /**
     * Determines the content type by comparing appFile with componentName
     */
    static determineContentType(appFile: string, componentName: string, componentHtml: string, paramJson: JsonObject): string {
        // Check for explicit ContentType override in paramJson
        if (paramJson && paramJson.containsKey('ContentType')) {
            const contentType = paramJson.getString('ContentType');
            if (contentType && contentType.length > 0) {
                return contentType;
            }
        }

        // 1. AppFile: appFile matches componentName (root component)
        if (appFile.toLowerCase() === componentName.toLowerCase()) {
            return ContentTypeAppFile;
        }

        // 2. SlotHolder: Contains {{$HTMLPLACEHOLDER...}} pattern
        if (componentHtml.toUpperCase().indexOf('{{$HTMLPLACEHOLDER') >= 0) {
            return ContentTypeSlotHolder;
        }

        // 3. Placeholder: Default for all other components
        return ContentTypePlaceholder;
    }

    /**
     * Recursively extracts component information from HtmlJson structure
     */
    static extractComponentsFromHtmlJson(htmlJson: JsonObject, placeholders: string[], slotHolders: string[], slots: string[], jsonArrays: string[], jsonKeys: string[]): void {
        if (!htmlJson) {
            return;
        }

        for (const key of htmlJson.keys()) {
            if (key === META_KEY) continue;

            const jsonVal: JsonValue = htmlJson.get(key);

            // Determine type using new or old format
            let typeChar = '\0';
            let keyName = '';

            if (isTypePrefixedKey(key)) {
                typeChar = key[0];
                const underscoreIdx = key.indexOf('_');
                keyName = underscoreIdx >= 0 ? key.substring(underscoreIdx + 1) : '';
            } else {
                const underscoreIndex = key.indexOf('_');
                const oldKeyType = underscoreIndex >= 0 ? key.substring(underscoreIndex + 1) : key;

                if (oldKeyType.startsWith('Placeholder_')) {
                    typeChar = 'p';
                    keyName = oldKeyType.substring('Placeholder_'.length);
                } else if (oldKeyType.startsWith('Placeholder')) {
                    typeChar = 'p';
                    keyName = '';
                } else if (oldKeyType.startsWith('Slotholder_')) {
                    typeChar = 'h';
                    keyName = oldKeyType.substring('Slotholder_'.length);
                } else if (oldKeyType.startsWith('Slotholder')) {
                    typeChar = 'h';
                    keyName = '';
                } else if (oldKeyType.startsWith('Slot_')) {
                    typeChar = 'i';
                    keyName = oldKeyType.substring('Slot_'.length);
                } else if (oldKeyType.startsWith('JsonKey_')) {
                    typeChar = 'k';
                    keyName = oldKeyType.substring('JsonKey_'.length);
                } else if (oldKeyType.startsWith('JsonKey')) {
                    typeChar = 'k';
                    keyName = '';
                } else if (oldKeyType.startsWith('ArrayBlock_')) {
                    typeChar = 'a';
                    keyName = oldKeyType.substring('ArrayBlock_'.length);
                } else if (oldKeyType.startsWith('ArrayBlock')) {
                    typeChar = 'a';
                    keyName = '';
                } else if (oldKeyType.startsWith('ConditionalBlock')) {
                    typeChar = 'c';
                    keyName = '';
                }
            }

            const strVal = jsonVal.asString();
            const objVal = jsonVal.asObject();

            // Process based on type
            if (typeChar === 'p' || typeChar === 'P') {
                if (typeChar === 'P') {
                    continue;
                }
                let name = keyName;
                if (!name && strVal && strVal.length >= 4) {
                    name = strVal.substring(2, strVal.length - 2);
                }
                if (name && !placeholders.includes(name)) {
                    placeholders.push(name);
                }
            } else if (typeChar === 'h' || typeChar === 'H') {
                if (objVal) {
                    const slotName = objVal.get('SlotName').asString() ?? '';
                    if (slotName && !slotHolders.includes(slotName)) {
                        slotHolders.push(slotName);
                    }
                    RuleView.extractComponentsFromHtmlJson(objVal, placeholders, slotHolders, slots, jsonArrays, jsonKeys);
                }
            } else if (typeChar === 'i') {
                let slotKeyName = keyName;
                if (!slotKeyName && strVal && strVal.length >= 4) {
                    slotKeyName = strVal.substring(3, strVal.length - 2);
                }
                if (slotKeyName && !slots.includes(slotKeyName)) {
                    slots.push(slotKeyName);
                }
            } else if (typeChar === 'k' || typeChar === 'K') {
                if (typeChar === 'K') {
                    continue;
                }
                if (keyName && keyName.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                    if (!slots.includes(keyName)) {
                        slots.push(keyName);
                    }
                } else {
                    let jsonKeyName = keyName;
                    if (!jsonKeyName && strVal && strVal.length >= 6) {
                        jsonKeyName = strVal.substring(3, strVal.length - 2);
                    }
                    if (jsonKeyName && !jsonKeys.includes(jsonKeyName)) {
                        jsonKeys.push(jsonKeyName);
                    }
                }
            } else if (typeChar === 'a' || typeChar === 'A') {
                if (typeChar !== 'A' && keyName && !jsonArrays.includes(keyName)) {
                    jsonArrays.push(keyName);
                }
                if (objVal) {
                    RuleView.extractComponentsFromHtmlJson(objVal, placeholders, slotHolders, slots, jsonArrays, jsonKeys);
                }
            } else if (typeChar === 'c') {
                if (objVal) {
                    const conditionalKey = objVal.get('ConditionalKey').asString() ?? '';
                    if (conditionalKey && !jsonArrays.includes(conditionalKey)) {
                        jsonArrays.push(conditionalKey);
                    }
                    RuleView.extractComponentsFromHtmlJson(objVal, placeholders, slotHolders, slots, jsonArrays, jsonKeys);
                }
            } else if (objVal) {
                RuleView.extractComponentsFromHtmlJson(objVal, placeholders, slotHolders, slots, jsonArrays, jsonKeys);
            }
        }
    }

    /**
     * Returns whether the source template carries INLINE STYLING that RuleView forbids.
     *
     * The invariant this protects is presentation ownership: a rule component declares STRUCTURE,
     * and RuleView generates the styling and the diagnostic chrome from it. A template that styles
     * itself would defeat that auto-generation, which is why style= is rejected.
     *
     * Structural tags are NOT a violation and are no longer rejected. The check previously refused
     * div/span/p outright, which conflated "carries markup" with "carries styling" and made it
     * impossible to mark up a region for any reason. An unstyled span with only a class injects no
     * presentation, so it is allowed; class attributes are likewise fine because the stylesheet,
     * not the template, decides what they mean.
     *
     * The failure mode is destructive by design — getView replaces the WHOLE component with the
     * HavingHTMLStyle placeholder. That substitution is silent in a byte compare (it renders as a
     * stable string and never fails a gate), so this predicate must stay narrow: every tag it
     * rejects is content a template author loses without an error.
     */
    static hasHtmlStyleTags(html: string): boolean {
        if (!html || html.length === 0) {
            return false;
        }

        // Skip validation if content is from RuleView's own output (has RuleView marker pattern).
        // RuleView generates divs with id="..." style="..." pattern, so without this guard
        // re-processing its own output would trip the style= rejection below.
        if (html.toLowerCase().includes('<div id="')) {
            return false;
        }

        const lowerHtml = html.toLowerCase();

        // Inline styling is the ONLY violation: it is what would override RuleView's generated presentation.
        if (lowerHtml.includes('style=')) {
            return true;
        }

        return false;
    }

    /**
     * Generates auto description based on content type and template content
     */
    static generateAutoDescription(contentType: string, componentName: string, html: string, appSite: string, resolvedAppSite: string, searchAppSiteColor: string): string | null {
        const placeholders = RuleView.extractPlaceholders(html);
        const slotHolders = RuleView.extractSlotHolders(html);
        const slots = RuleView.extractSlots(html);
        const jsonArrays = RuleView.extractJsonArrayAndConditionalNames(html);
        const jsonKeys = RuleView.extractJsonKeys(html);

        return RuleView.buildAutoDescription(contentType, componentName, appSite, resolvedAppSite, searchAppSiteColor, placeholders, slotHolders, slots, jsonArrays, jsonKeys);
    }

    static extractSlots(html: string): string[] {
        const slots: string[] = [];
        if (!html || html.length === 0) return slots;

        let index = 0;
        while (index < html.length) {
            const start = html.indexOf('{{$', index);
            if (start < 0) break;
            const end = html.indexOf('}}', start + 3);
            if (end < 0) break;
            const slotName = html.substring(start + 3, end);
            if (slotName.length > 0 && !slots.includes(slotName)) {
                slots.push(slotName);
            }
            index = end + 2;
        }
        return slots;
    }

    static extractPlaceholders(html: string): string[] {
        const placeholders: string[] = [];
        if (!html || html.length === 0) return placeholders;

        let index = 0;
        while (index < html.length) {
            const start = html.indexOf('{{', index);
            if (start < 0) break;
            const end = html.indexOf('}}', start + 2);
            if (end < 0) break;
            const content = html.substring(start + 2, end);
            if (content.length > 0 &&
                content[0] !== '#' &&
                content[0] !== '/' &&
                content[0] !== '@' &&
                content[0] !== '$' &&
                content[0] !== '^' &&
                content[0] !== '!' &&
                content[0] !== '?') {
                if (!placeholders.includes(content)) {
                    placeholders.push(content);
                }
            }
            index = end + 2;
        }
        return placeholders;
    }

    static extractSlotHolders(html: string): string[] {
        const slotHolders: string[] = [];
        if (!html || html.length === 0) return slotHolders;

        let index = 0;
        while (index < html.length) {
            const start = html.indexOf('{{#', index);
            const skipStart = html.indexOf('{{!#', index);

            let actualStart: number;
            let nameOffset: number;
            if (start < 0 && skipStart < 0) {
                break;
            } else if (start < 0) {
                actualStart = skipStart;
                nameOffset = 4;
            } else if (skipStart < 0) {
                actualStart = start;
                nameOffset = 3;
            } else if (skipStart < start) {
                actualStart = skipStart;
                nameOffset = 4;
            } else {
                actualStart = start;
                nameOffset = 3;
            }

            const end = html.indexOf('}}', actualStart + nameOffset);
            if (end < 0) break;

            const slotName = html.substring(actualStart + nameOffset, end);
            if (slotName.length > 0 && !slotHolders.includes(slotName)) {
                slotHolders.push(slotName);
            }
            index = end + 2;
        }
        return slotHolders;
    }

    static extractJsonKeys(html: string): string[] {
        const jsonKeys: string[] = [];
        if (!html || html.length === 0) return jsonKeys;

        let index = 0;
        while (index < html.length) {
            const start = html.indexOf('{{$', index);
            if (start < 0) break;
            const end = html.indexOf('}}', start + 3);
            if (end < 0) break;
            const keyName = html.substring(start + 3, end);
            if (keyName.length > 0 &&
                !keyName.toUpperCase().startsWith('HTMLPLACEHOLDER') &&
                !jsonKeys.includes(keyName)) {
                jsonKeys.push(keyName);
            }
            index = end + 2;
        }
        return jsonKeys;
    }

    static extractJsonArrayAndConditionalNames(html: string): string[] {
        const names: string[] = [];
        if (!html || html.length === 0) return names;

        let index = 0;
        while (index < html.length) {
            const arrayStart = html.indexOf('{{@', index);
            const condStart = html.indexOf('{{?', index);
            // {{^Key}} must be scanned too. A STANDALONE negative block (no opening form for the same
            // key) is otherwise invisible here, so the Normal-path description omits a key that the
            // HtmlJson path reports — the two descriptions then disagree.
            const negStart = html.indexOf('{{^', index);

            let start = -1;
            if (arrayStart >= 0) start = arrayStart;
            if (condStart >= 0 && (start < 0 || condStart < start)) start = condStart;
            if (negStart >= 0 && (start < 0 || negStart < start)) start = negStart;
            if (start < 0) break;

            const end = html.indexOf('}}', start + 3);
            if (end < 0) break;

            const name = html.substring(start + 3, end);

            // A {{^Key}} only counts on its own when no opening form for the same key exists.
            // The SKIP forms matter here: the HtmlJson path deliberately omits skip-marked arrays
            // from this list, so counting a skip-array's negative block would disagree with it.
            const negativeOwnedByOpeningBlock = start === negStart
                && (html.includes('{{@' + name + '}}')
                    || html.includes('{{?' + name + '}}')
                    || html.includes('{{!@' + name + '}}')
                    || html.includes('{{!?' + name + '}}'));

            if (name.length > 0 &&
                !negativeOwnedByOpeningBlock &&
                !name.toUpperCase().startsWith('HTMLPLACEHOLDER') &&
                !names.includes(name)) {
                names.push(name);
            }
            index = end + 2;
        }
        return names;
    }

    static wrapWithStyle(html: string, contentType: string, componentId: string, title: string, description: string | null, borderColor: string, backgroundGradient: string): string {
        switch (contentType) {
            case ContentTypeAppFile:
                return RuleView.wrapAppFileStyle(html, componentId, title, description, borderColor, backgroundGradient);
            case ContentTypePlaceholder:
                return RuleView.wrapPlaceholderStyle(html, componentId, description, borderColor);
            case ContentTypeSlotHolder:
                return RuleView.wrapSlotHolderStyle(html, componentId, description, borderColor);
            case ContentTypeSlot:
                return RuleView.wrapSlotStyle(html, componentId, description, borderColor);
            default:
                return RuleView.wrapPlainStyle(html, componentId, borderColor);
        }
    }

    static wrapAppFileStyle(html: string, componentId: string, title: string, description: string | null, borderColor: string, backgroundGradient: string): string {
        const descriptionHtml = description
            ? `
        <p style="color: #666; margin: 0 0 15px 0; font-size: 0.95em;">${description}</p>`
            : '';

        return `${DecoratorStyleBlock}<div id="${componentId}" class="av-dec-outer" role="region" aria-label="${title}" style="max-width: 800px; margin: 40px auto; padding: 30px; background: ${backgroundGradient}; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.1);">
    <div style="background: white; padding: 30px; border-radius: 8px;">
        <h2 style="color: ${borderColor}; margin: 0 0 10px 0; font-size: 1.8em;">${title}</h2>${descriptionHtml}
        <div class="av-dec" style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid ${borderColor};">
            ${html}
        </div>
    </div>
</div>`;
    }

    static wrapPlaceholderStyle(html: string, componentId: string, description: string | null, borderColor: string): string {
        return `<div id="${componentId}" role="region" aria-label="${description}" style="border: 2px dashed ${borderColor}; border-radius: 6px; padding: 5px; background: #eff6ff;">
    <p style="margin: 0; text-align: center; color: ${borderColor}; font-weight: 500;">
        ${description}
    </p>
    <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid ${borderColor};">
        ${html}
    </div>
</div>`;
    }

    static wrapSlotHolderStyle(html: string, componentId: string, description: string | null, borderColor: string): string {
        return `<div id="${componentId}" role="region" aria-label="${description}" style="border: 2px dashed ${borderColor}; border-radius: 6px; padding: 5px; background: #ecfdf5;">
    <p style="margin: 0; text-align: center; color: ${borderColor}; font-weight: 500;">
        ${description}
    </p>
    <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid ${borderColor};">
        ${html}
    </div>
</div>`;
    }

    static wrapSlotStyle(html: string, componentId: string, description: string | null, borderColor: string): string {
        return `<div id="${componentId}" role="region" aria-label="${description}" style="border: 1px solid ${borderColor}; border-radius: 4px; padding: 8px; margin: 4px 0; background: #ecfeff;">
    <p style="margin: 0 0 5px 0; font-size: 0.85em; color: ${borderColor}; font-weight: 500;">
        ${description}
    </p>
    <div style="background: white; padding: 8px; border-radius: 4px;">
        ${html}
    </div>
</div>`;
    }

    static wrapPlainStyle(html: string, componentId: string, borderColor: string): string {
        return `<div id="${componentId}" style="border-left: 4px solid ${borderColor}; padding: 10px; margin: 5px 0;">
    ${html}
</div>`;
    }

    static wrapJsonKeys(html: string, borderColor: string, skipJsonKeys: Set<string>): string {
        let wrappedHtml = html;
        let index = 0;
        while (index < wrappedHtml.length) {
            const start = wrappedHtml.indexOf('{{$', index);
            if (start < 0) break;

            const end = wrappedHtml.indexOf('}}', start + 3);
            if (end < 0) break;

            const keyName = wrappedHtml.substring(start + 3, end);

            if (keyName.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                index = end + 2;
                continue;
            }

            if (skipJsonKeys.has(keyName)) {
                index = end + 2;
                continue;
            }

            if (RuleView.isInsideJsonArray(wrappedHtml, start)) {
                index = end + 2;
                continue;
            }

            if (RuleView.isInsideSelectElement(wrappedHtml, start)) {
                index = end + 2;
                continue;
            }

            if (RuleView.isInsideButtonElement(wrappedHtml, start)) {
                index = end + 2;
                continue;
            }

            const placeholder = wrappedHtml.substring(start, end + 2);
            const wrappedKey = `<div id="${RuleView.toCamelCase(keyName)}" style="border: 2px dashed ${borderColor}; border-radius: 6px; padding: 5px; margin: 5px 0; background: #fffbeb;">
    <p style="margin: 0 0 5px 0; color: ${borderColor}; font-weight: 500; font-size: 0.85em;">JsonKey [${keyName}]</p>
    <div style="background: white; padding: 8px; border-radius: 4px;">${placeholder}</div>
</div>`;

            wrappedHtml = wrappedHtml.substring(0, start) + wrappedKey + wrappedHtml.substring(end + 2);
            index = start + wrappedKey.length;
        }

        return wrappedHtml;
    }

    static isInsideJsonArray(html: string, position: number): boolean {
        let searchPos = position - 1;

        while (searchPos >= 0) {
            const arrayStart = html.lastIndexOf('{{@', searchPos);
            if (arrayStart < 0) return false;

            const arrayNameEnd = html.indexOf('}}', arrayStart + 3);
            if (arrayNameEnd < 0) return false;

            const arrayName = html.substring(arrayStart + 3, arrayNameEnd);

            if (arrayName.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                searchPos = arrayStart - 1;
                continue;
            }

            const closeMarker = '{{/' + arrayName + '}}';
            const arrayClose = html.indexOf(closeMarker, arrayNameEnd + 2);

            if (arrayClose < 0 || arrayClose > position) {
                return true;
            }

            searchPos = arrayStart - 1;
        }

        return false;
    }

    static isInsideSelectElement(html: string, position: number): boolean {
        let searchPos = position - 1;

        while (searchPos >= 0) {
            const lowerHtml = html.toLowerCase();
            const selectStart = lowerHtml.lastIndexOf('<select', searchPos);
            if (selectStart < 0) return false;

            const selectClose = lowerHtml.indexOf('</select>', selectStart);

            if (selectClose < 0 || selectClose > position) {
                return true;
            }

            searchPos = selectStart - 1;
        }

        return false;
    }

    static findEnclosingSelectStart(html: string, position: number): number {
        let searchPos = position - 1;

        while (searchPos >= 0) {
            const lowerHtml = html.toLowerCase();
            const selectStart = lowerHtml.lastIndexOf('<select', searchPos);
            if (selectStart < 0) return -1;

            const selectClose = lowerHtml.indexOf('</select>', selectStart);

            if (selectClose < 0 || selectClose > position) {
                return selectStart;
            }

            searchPos = selectStart - 1;
        }

        return -1;
    }

    static findEnclosingSelectEnd(html: string, position: number): number {
        const lowerHtml = html.toLowerCase();
        const selectClose = lowerHtml.indexOf('</select>', position);
        if (selectClose < 0) return -1;

        return selectClose + '</select>'.length;
    }

    static styleSelectElement(selectHtml: string, borderColor: string): string {
        const selectTagEnd = selectHtml.indexOf('>');
        if (selectTagEnd < 0) return selectHtml;

        const selectStyle = `width: 100%; padding: 8px 12px; border: 2px solid ${borderColor}; border-radius: 4px; background: white; font-size: 0.95em; color: #495057; cursor: pointer;`;

        const stylePos = selectHtml.substring(0, selectTagEnd).toLowerCase().indexOf('style=');
        if (stylePos >= 0) {
            const styleValueStart = selectHtml.indexOf('"', stylePos);
            if (styleValueStart >= 0 && styleValueStart < selectTagEnd) {
                return selectHtml.substring(0, styleValueStart + 1) + selectStyle + ' ' + selectHtml.substring(styleValueStart + 1);
            }
        }

        const selectTagStart = selectHtml.toLowerCase().indexOf('<select');
        if (selectTagStart < 0) return selectHtml;
        const insertPos = selectTagStart + 7;
        return selectHtml.substring(0, insertPos) + ` style="${selectStyle}"` + selectHtml.substring(insertPos);
    }

    static wrapJsonArrays(html: string, borderColor: string): string {
        let wrappedHtml = html;
        let index = 0;

        while (index < wrappedHtml.length) {
            const startMarkerBegin = wrappedHtml.indexOf('{{@', index);
            if (startMarkerBegin < 0) break;

            const startMarkerEnd = wrappedHtml.indexOf('}}', startMarkerBegin + 3);
            if (startMarkerEnd < 0) break;

            const arrayName = wrappedHtml.substring(startMarkerBegin + 3, startMarkerEnd);

            if (arrayName.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                index = startMarkerEnd + 2;
                continue;
            }

            const endMarker = '{{/' + arrayName + '}}';
            const endMarkerPos = wrappedHtml.indexOf(endMarker, startMarkerEnd + 2);
            if (endMarkerPos < 0) {
                index = startMarkerEnd + 2;
                continue;
            }

            let arrayBlockEnd = endMarkerPos + endMarker.length;

            const emptyMarker = '{{^' + arrayName + '}}';
            let scanPos = arrayBlockEnd;
            while (scanPos < wrappedHtml.length) {
                const ch = wrappedHtml[scanPos];
                if (ch !== ' ' && ch !== '\t' && ch !== '\n' && ch !== '\r') break;
                scanPos++;
            }

            if (scanPos + emptyMarker.length <= wrappedHtml.length
                && wrappedHtml.substring(scanPos, scanPos + emptyMarker.length) === emptyMarker) {
                const emptyEndPos = wrappedHtml.indexOf(endMarker, scanPos + emptyMarker.length);
                if (emptyEndPos >= 0) {
                    arrayBlockEnd = emptyEndPos + endMarker.length;
                }
            }

            if (RuleView.isInsideSelectElement(wrappedHtml, startMarkerBegin)) {
                const selectStart = RuleView.findEnclosingSelectStart(wrappedHtml, startMarkerBegin);
                const selectEnd = RuleView.findEnclosingSelectEnd(wrappedHtml, startMarkerBegin);

                if (selectStart >= 0 && selectEnd >= 0) {
                    const selectContent = wrappedHtml.substring(selectStart, selectEnd);
                    const styledSelect = RuleView.styleSelectElement(selectContent, borderColor);

                    const wrappedSelect = `<div id="${RuleView.toCamelCase(arrayName)}" style="border: 2px dashed ${borderColor}; border-radius: 6px; padding: 5px; margin: 5px 0; background: #f0fdfa;">
    <p style="margin: 0 0 5px 0; color: ${borderColor}; font-weight: 500; font-size: 0.85em;">JsonArray [${arrayName}]</p>
    <div style="background: white; padding: 8px; border-radius: 4px;">${styledSelect}</div>
</div>`;

                    wrappedHtml = wrappedHtml.substring(0, selectStart) + wrappedSelect + wrappedHtml.substring(selectEnd);
                    index = selectStart + wrappedSelect.length;
                    continue;
                }
            }

            if (RuleView.isInsideButtonElement(wrappedHtml, startMarkerBegin)) {
                const buttonStart = RuleView.findEnclosingButtonStart(wrappedHtml, startMarkerBegin);
                const buttonEnd = RuleView.findEnclosingButtonEnd(wrappedHtml, startMarkerBegin);

                if (buttonStart >= 0 && buttonEnd >= 0) {
                    const buttonContent = wrappedHtml.substring(buttonStart, buttonEnd);
                    const styledButton = RuleView.styleButtonElement(buttonContent, borderColor);

                    const wrappedButton = `<div id="${RuleView.toCamelCase(arrayName)}" style="border: 2px dashed ${borderColor}; border-radius: 6px; padding: 5px; margin: 5px 0; background: #f0fdfa;">
    <p style="margin: 0 0 5px 0; color: ${borderColor}; font-weight: 500; font-size: 0.85em;">JsonArray [${arrayName}]</p>
    <div style="background: white; padding: 8px; border-radius: 4px;">${styledButton}</div>
</div>`;

                    wrappedHtml = wrappedHtml.substring(0, buttonStart) + wrappedButton + wrappedHtml.substring(buttonEnd);
                    index = buttonStart + wrappedButton.length;
                    continue;
                }
            }

            // An array inside a <table> ({{@Columns}} in <thead>, {{@Rows}}/{{@Cells}} in <tbody>) is left
            // UNWRAPPED: a per-array annotation <div> injected inside <tr>/<thead>/<tbody> is illegal there
            // and the browser foster-parents it out of the table, detaching the label. The table's scroll
            // container and bordered/striped presentation come from the template's <div class="av-tablebox">
            // wrapper plus the `.av-dec table` / `.av-tablebox` rules in the decorator <style> block, so the
            // raw markers just merge in place and the table renders clean. Mirrored in getViewJson.
            if (RuleView.isInsideTableElement(wrappedHtml, startMarkerBegin)) {
                index = arrayBlockEnd;
                continue;
            }

            const arrayContent = wrappedHtml.substring(startMarkerBegin, arrayBlockEnd);

            const wrappedArray = `<div id="${RuleView.toCamelCase(arrayName)}" style="border: 2px dashed ${borderColor}; border-radius: 6px; padding: 5px; margin: 5px 0; background: #f0fdfa;">
    <p style="margin: 0 0 5px 0; color: ${borderColor}; font-weight: 500; font-size: 0.85em;">JsonArray [${arrayName}]</p>
    <div style="background: white; padding: 8px; border-radius: 4px;">${arrayContent}</div>
</div>`;

            wrappedHtml = wrappedHtml.substring(0, startMarkerBegin) + wrappedArray + wrappedHtml.substring(arrayBlockEnd);
            index = startMarkerBegin + wrappedArray.length;
        }

        return wrappedHtml;
    }

    static parseSkipKeys(skipKeysStr: string): Set<string> {
        const skipKeys = new Set<string>();
        if (!skipKeysStr || skipKeysStr.length === 0) return skipKeys;

        let start = 0;
        while (start < skipKeysStr.length) {
            const commaPos = skipKeysStr.indexOf(',', start);
            let key: string;
            if (commaPos < 0) {
                key = skipKeysStr.substring(start).trim();
                start = skipKeysStr.length;
            } else {
                key = skipKeysStr.substring(start, commaPos).trim();
                start = commaPos + 1;
            }
            if (key.length > 0) {
                skipKeys.add(key);
            }
        }

        return skipKeys;
    }

    static wrapConditionals(html: string, borderColor: string): string {
        let wrappedHtml = html;
        let index = 0;

        while (index < wrappedHtml.length) {
            // Anchor on {{?Key}} AND on a STANDALONE {{^Key}}. A negative block with no {{?Key}} or
            // {{@Key}} for the same key is a conditional in its own right; without this it goes
            // unwrapped here while the HtmlJson path wraps it, and the two descriptions disagree.
            const posStart = wrappedHtml.indexOf('{{?', index);
            const negStart = wrappedHtml.indexOf('{{^', index);

            let start: number;
            let isStandaloneNegative: boolean;
            if (posStart < 0 && negStart < 0) break;
            else if (posStart < 0) { start = negStart; isStandaloneNegative = true; }
            else if (negStart < 0) { start = posStart; isStandaloneNegative = false; }
            else if (posStart <= negStart) { start = posStart; isStandaloneNegative = false; }
            else { start = negStart; isStandaloneNegative = true; }

            const nameEnd = wrappedHtml.indexOf('}}', start + 3);
            if (nameEnd < 0) break;

            const keyName = wrappedHtml.substring(start + 3, nameEnd);

            if (keyName.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                index = nameEnd + 2;
                continue;
            }

            if (RuleView.isInsideJsonArray(wrappedHtml, start)) {
                index = nameEnd + 2;
                continue;
            }

            // A {{^Key}} that pairs with an array-open or a positive conditional for the same key is
            // not a standalone conditional — that block owns it. All four opening forms count,
            // including the SKIP-marked variants: {{!@Key}} does not contain the substring "{{@Key}}",
            // so checking only the unmarked forms would wrap a skip-array's negative block as a
            // conditional and diverge from the JSON engines.
            if (isStandaloneNegative
                && (wrappedHtml.includes('{{@' + keyName + '}}')
                    || wrappedHtml.includes('{{?' + keyName + '}}')
                    || wrappedHtml.includes('{{!@' + keyName + '}}')
                    || wrappedHtml.includes('{{!?' + keyName + '}}'))) {
                index = nameEnd + 2;
                continue;
            }

            const closeMarker = '{{/' + keyName + '}}';
            const falseMarker = '{{^' + keyName + '}}';

            const trueBlockEnd = wrappedHtml.indexOf(closeMarker, nameEnd + 2);
            if (trueBlockEnd < 0) {
                index = nameEnd + 2;
                continue;
            }

            let conditionalEnd = trueBlockEnd + closeMarker.length;
            let hasFalseBlock = false;

            let scanPos = conditionalEnd;
            while (scanPos < wrappedHtml.length) {
                const ch = wrappedHtml[scanPos];
                if (ch !== ' ' && ch !== '\t' && ch !== '\n' && ch !== '\r') break;
                scanPos++;
            }

            if (!isStandaloneNegative
                && scanPos + falseMarker.length <= wrappedHtml.length
                && wrappedHtml.substring(scanPos, scanPos + falseMarker.length) === falseMarker) {
                const falseBlockEnd = wrappedHtml.indexOf(closeMarker, scanPos + falseMarker.length);
                if (falseBlockEnd >= 0) {
                    conditionalEnd = falseBlockEnd + closeMarker.length;
                    hasFalseBlock = true;
                }
            }

            const conditionalContent = wrappedHtml.substring(start, conditionalEnd);

            const label = hasFalseBlock
                ? `Conditional [${keyName}] (True/False)`
                : `Conditional [${keyName}]`;

            const wrappedConditional = `<div id="${RuleView.toCamelCase(keyName)}" style="border: 2px dashed ${borderColor}; border-radius: 6px; padding: 5px; margin: 5px 0; background: #fff1f2;">`
                + `\n    <p style="margin: 0 0 5px 0; color: ${borderColor}; font-weight: 500; font-size: 0.85em;">${label}</p>`
                + `\n    <div style="background: white; padding: 8px; border-radius: 4px;">${conditionalContent}</div>`
                + `\n</div>`;

            wrappedHtml = wrappedHtml.substring(0, start) + wrappedConditional + wrappedHtml.substring(conditionalEnd);
            index = start + wrappedConditional.length;
        }

        return wrappedHtml;
    }

    static wrapSlotMarkers(html: string, borderColor: string): string {
        let wrappedHtml = html;
        let index = 0;

        while (index < wrappedHtml.length) {
            const start = wrappedHtml.toUpperCase().indexOf('{{$HTMLPLACEHOLDER', index);
            if (start < 0) break;

            const end = wrappedHtml.indexOf('}}', start + 3);
            if (end < 0) break;

            const slotName = wrappedHtml.substring(start + 3, end);
            const marker = wrappedHtml.substring(start, end + 2);

            const wrappedSlot = `<div style="border: 1px solid ${borderColor}; border-radius: 4px; padding: 8px; margin: 4px 0; background: #ecfeff;">`
                + `\n    <p style="margin: 0 0 5px 0; font-size: 0.85em; color: ${borderColor}; font-weight: 500;">`
                + `\n        Slot [${slotName}]`
                + `\n    </p>`
                + `\n    <div style="background: white; padding: 8px; border-radius: 4px;">`
                + `\n        ${marker}`
                + `\n    </div>`
                + `\n</div>`;

            wrappedHtml = wrappedHtml.substring(0, start) + wrappedSlot + wrappedHtml.substring(end + 2);
            index = start + wrappedSlot.length;
        }

        return wrappedHtml;
    }

    static styleAllSelectElements(html: string, borderColor: string): string {
        let result = html;
        let index = 0;

        const selectStyle = `width: 100%; padding: 8px 12px; border: 2px solid ${borderColor}; border-radius: 4px; background: white; font-size: 0.95em; color: #495057; cursor: pointer;`;

        while (index < result.length) {
            const selectStart = result.toLowerCase().indexOf('<select', index);
            if (selectStart < 0) break;

            const selectTagEnd = result.indexOf('>', selectStart);
            if (selectTagEnd < 0) break;

            const tagContent = result.substring(selectStart, selectTagEnd).toLowerCase();
            if (tagContent.indexOf('style=') >= 0) {
                index = selectTagEnd + 1;
                continue;
            }

            const insertPos = selectStart + 7;
            const styleAttr = ` style="${selectStyle}"`;
            result = result.substring(0, insertPos) + styleAttr + result.substring(insertPos);
            index = insertPos + styleAttr.length;
        }

        return result;
    }

    static isInsideButtonElement(html: string, position: number): boolean {
        let searchPos = position - 1;

        while (searchPos >= 0) {
            const lowerHtml = html.toLowerCase();
            const buttonStart = lowerHtml.lastIndexOf('<button', searchPos);
            if (buttonStart < 0) return false;

            const buttonClose = lowerHtml.indexOf('</button>', buttonStart);

            if (buttonClose < 0 || buttonClose > position) {
                return true;
            }

            searchPos = buttonStart - 1;
        }

        return false;
    }

    /**
     * Returns whether the specified character position falls inside a <table> element. Walk backwards for a
     * <table that doesn't have a matching </table> before position. Pure string scan, no HTML parser.
     */
    static isInsideTableElement(html: string, position: number): boolean {
        let searchPos = position - 1;

        while (searchPos >= 0) {
            const lowerHtml = html.toLowerCase();
            const tableStart = lowerHtml.lastIndexOf('<table', searchPos);
            if (tableStart < 0) return false;

            const tableClose = lowerHtml.indexOf('</table>', tableStart);

            if (tableClose < 0 || tableClose > position) {
                return true;
            }

            searchPos = tableStart - 1;
        }

        return false;
    }

    /**
     * Returns whether the HtmlJson entries emitted so far leave a <table> open — i.e. the next array block is
     * table-hosted. The literal HTML lives only in the 's' entries (the '<table>' opener among them);
     * concatenating them in order and testing the end reuses isInsideTableElement, the same predicate the
     * string engine applies. Used by getViewJson to leave table arrays UNWRAPPED.
     */
    static isPrecededByOpenTableJson(result: JsonObject): boolean {
        let html = '';
        for (const key of result.keys()) {
            if (key.length > 0 && key[0] === 's') {
                const val = result.get(key);
                if (val.isString) {
                    html += val.getString();
                }
            }
        }
        return RuleView.isInsideTableElement(html, html.length);
    }

    static findEnclosingButtonStart(html: string, position: number): number {
        let searchPos = position - 1;

        while (searchPos >= 0) {
            const lowerHtml = html.toLowerCase();
            const buttonStart = lowerHtml.lastIndexOf('<button', searchPos);
            if (buttonStart < 0) return -1;

            const buttonClose = lowerHtml.indexOf('</button>', buttonStart);

            if (buttonClose < 0 || buttonClose > position) {
                return buttonStart;
            }

            searchPos = buttonStart - 1;
        }

        return -1;
    }

    static findEnclosingButtonEnd(html: string, position: number): number {
        const lowerHtml = html.toLowerCase();
        const buttonClose = lowerHtml.indexOf('</button>', position);
        if (buttonClose < 0) return -1;

        return buttonClose + '</button>'.length;
    }

    static styleButtonElement(buttonHtml: string, borderColor: string): string {
        const buttonTagStart = buttonHtml.toLowerCase().indexOf('<button');
        if (buttonTagStart < 0) return buttonHtml;

        const buttonTagEnd = buttonHtml.indexOf('>', buttonTagStart);
        if (buttonTagEnd < 0) return buttonHtml;

        const buttonStyle = `padding: 8px 16px; border: 2px solid ${borderColor}; border-radius: 4px; background: white; font-size: 0.95em; color: #495057; cursor: pointer;`;

        const tagContent = buttonHtml.substring(buttonTagStart, buttonTagEnd).toLowerCase();
        const stylePos = tagContent.indexOf('style=');
        if (stylePos >= 0) {
            const styleValueStart = buttonHtml.indexOf('"', buttonTagStart + stylePos);
            if (styleValueStart >= 0 && styleValueStart < buttonTagEnd) {
                return buttonHtml.substring(0, styleValueStart + 1) + buttonStyle + ' ' + buttonHtml.substring(styleValueStart + 1);
            }
        }

        const insertPos = buttonTagStart + 7;
        return buttonHtml.substring(0, insertPos) + ` style="${buttonStyle}"` + buttonHtml.substring(insertPos);
    }

    static findAndRemovePrecedingElement(result: JsonObject, currentSequence: number, elementType: string): string | null {
        const openTag = '<' + elementType;

        for (let seq = currentSequence - 1; seq >= 1; seq--) {
            const key = `s${seq}`;
            if (!result.has(key)) {
                continue;
            }

            const htmlContent = result.get(key).asString();
            if (htmlContent === null) {
                continue;
            }

            const elementPos = htmlContent.toLowerCase().indexOf(openTag);
            if (elementPos < 0) {
                continue;
            }

            // Check if element is closed in this same entry
            const closeTag = '</' + elementType + '>';
            const closePos = htmlContent.toLowerCase().indexOf(closeTag, elementPos);
            if (closePos >= 0) {
                break;
            }

            // Check if the opening tag is already balanced by a closing tag in a LATER 's' entry. A
            // select/button opening absorbed into a prior array wrapper keeps its opening tag in the array-box
            // entry while its close is emitted in a following entry (the absorb-close flow). Such an opening is
            // already consumed and must NOT be relocated again into this array, or it is stolen from the
            // wrapper that owns it. Treat "closed in a later entry" exactly like "closed in same entry".
            if (RuleView.isClosedInLaterEntry(result, seq, currentSequence, closeTag)) {
                break;
            }

            // Extract: content before element stays, element opening goes into return value
            const beforeElement = htmlContent.substring(0, elementPos);
            const elementHtml = htmlContent.substring(elementPos);

            result.set(key, beforeElement);

            return elementHtml;
        }

        return null;
    }

    /**
     * Returns true when a matching closeTag (e.g. </select>) appears in an 's' entry emitted AFTER openSeq
     * and before currentSequence. Used to detect an opening tag that a prior array already absorbed: its
     * opening lives in the array-box entry and its close in a later entry, so the tag is already consumed and
     * must not be relocated a second time by a following array.
     */
    static isClosedInLaterEntry(result: JsonObject, openSeq: number, currentSequence: number, closeTag: string): boolean {
        for (let seq = openSeq + 1; seq < currentSequence; seq++) {
            const key = `s${seq}`;
            if (!result.has(key)) {
                continue;
            }
            const html = result.get(key).asString();
            if (html === null) {
                continue;
            }
            if (html.toLowerCase().indexOf(closeTag) >= 0) {
                return true;
            }
        }
        return false;
    }

    static tryRelocateSplitPrecedingElement(result: JsonObject, sequence: number, elementType: string, arrayOpening: string, borderColor: string): { relocated: boolean; sequence: number } {
        const openTag = '<' + elementType;
        const closeTag = '</' + elementType + '>';
        const orderedKeys = [...result.keys()];

        let openKey: string | null = null;
        let elementPos = -1;
        for (let i = orderedKeys.length - 1; i >= 0; i--) {
            const key = orderedKeys[i];
            if (!key || key[0] !== 's') continue;
            const html = result.get(key).asString();
            if (html === null) continue;

            const lowerHtml = html.toLowerCase();
            const pos = lowerHtml.indexOf(openTag);
            if (pos < 0) continue;
            if (lowerHtml.indexOf(closeTag, pos) >= 0) {
                return { relocated: false, sequence };
            }
            if (html.indexOf('>', pos) >= 0) {
                return { relocated: false, sequence };
            }
            openKey = key;
            elementPos = pos;
            break;
        }

        if (openKey === null) {
            return { relocated: false, sequence };
        }

        // Guard: if a matching close tag already appears in an entry AFTER openKey, this split opening was
        // already absorbed into a prior array wrapper (opening in the box entry, close in a later entry).
        // Relocating it again would steal it from the wrapper that owns it — the same defect the contiguous
        // path guards against via isClosedInLaterEntry. Bail untouched.
        const openKeyIdx = orderedKeys.indexOf(openKey);
        for (let i = openKeyIdx + 1; i < orderedKeys.length; i++) {
            const k = orderedKeys[i];
            if (!k || k[0] !== 's') continue;
            const kHtml = result.get(k).asString();
            if (kHtml !== null && kHtml.toLowerCase().indexOf(closeTag) >= 0) {
                return { relocated: false, sequence };
            }
        }

        const openIdx = orderedKeys.indexOf(openKey);
        const continuation: string[] = [];
        let gtKey: string | null = null;
        for (let i = openIdx + 1; i < orderedKeys.length; i++) {
            const key = orderedKeys[i];
            continuation.push(key);
            const value = result.get(key).asString();
            if (value !== null && value.indexOf('>') >= 0) {
                gtKey = key;
                break;
            }
        }

        if (gtKey === null) {
            return { relocated: false, sequence };
        }

        const openHtml = result.get(openKey).asString() ?? '';
        const beforeElement = openHtml.substring(0, elementPos);
        const elementFragment = openHtml.substring(elementPos);
        result.set(openKey, beforeElement);

        const saved = continuation.map(key => ({ key, value: result.get(key) }));
        for (const key of continuation) {
            result.delete(key);
        }

        const styledFragment = RuleView.styleSplitOpenTagFragment(elementFragment, elementType, borderColor);
        result.set(`s${sequence}`, arrayOpening + styledFragment);
        sequence++;

        for (const savedEntry of saved) {
            const keyType = savedEntry.key.length > 0 ? savedEntry.key[0] : 's';
            const underscorePos = savedEntry.key.indexOf('_');
            const keyName = underscorePos >= 0 ? savedEntry.key.substring(underscorePos + 1) : '';

            if (savedEntry.key === gtKey) {
                const s = savedEntry.value.asString();
                if (s !== null) {
                    const gt = s.indexOf('>');
                    const upToClose = s.substring(0, gt + 1);
                    const afterClose = s.substring(gt + 1);
                    result.set(`s${sequence}`, upToClose);
                    sequence++;
                    if (afterClose.length > 0) {
                        result.set(`s${sequence}`, afterClose);
                        sequence++;
                    }
                    continue;
                }
            }

            const newKey = !keyName ? `${keyType}${sequence}` : `${keyType}${sequence}_${keyName}`;
            result.set(newKey, savedEntry.value);
            sequence++;
        }

        return { relocated: true, sequence };
    }

    static styleSplitOpenTagFragment(fragment: string, elementType: string, borderColor: string): string {
        const openTag = '<' + elementType;
        const lowerFragment = fragment.toLowerCase();
        const tagStart = lowerFragment.indexOf(openTag);
        if (tagStart < 0) {
            return fragment;
        }
        if (lowerFragment.indexOf('style=', tagStart) >= 0) {
            return fragment;
        }

        const style = elementType === 'select'
            ? `width: 100%; padding: 8px 12px; border: 2px solid ${borderColor}; border-radius: 4px; background: white; font-size: 0.95em; color: #495057; cursor: pointer;`
            : `display: block; width: 100%; padding: 10px 20px; border: 2px solid ${borderColor}; border-radius: 6px; background: linear-gradient(135deg, ${borderColor}22 0%, ${borderColor}11 100%); font-size: 1.1em; color: ${borderColor}; cursor: pointer; font-weight: 600; min-height: 44px; transition: all 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.1);`;

        const insertPos = tagStart + openTag.length;
        return fragment.substring(0, insertPos) + ` style="${style}"` + fragment.substring(insertPos);
    }

    static toCamelCase(str: string): string {
        if (!str || str.length === 0) return str;
        if (str[0] === str[0].toLowerCase()) return str;
        return str[0].toLowerCase() + str.substring(1);
    }

    static getStringParam(paramJson: JsonObject, key: string, defaultValue: string): string {
        if (!paramJson || !paramJson.containsKey(key)) return defaultValue;
        const value = paramJson.getString(key);
        return (value && value.length > 0) ? value : defaultValue;
    }

    static getOptionalStringParam(paramJson: JsonObject, key: string): string | null {
        if (!paramJson || !paramJson.containsKey(key)) return null;
        const value = paramJson.getString(key);
        return (value && value.length > 0) ? value : null;
    }
}
